import { DateTime } from "luxon";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import type { GridDates } from "../types";
import { DAYS_PER_WEEK } from "./suggestedHires";
import { dayIdxToColumn } from "./weekStart";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The short form used for a single day: "21 Sep". Shares its month token with the week range. */
const DAY_DATE_FORMAT = "d MMM";

/** Coerce one entry of the payload's `dates`. `undefined` when the index is past the end or unparseable. */
export const toGridDateTime = (dates: GridDates, index: number): DateTime | undefined => {
	const value = dates[index];
	if (value === undefined) {
		return undefined;
	}
	if (DateTime.isDateTime(value)) {
		return value.isValid ? value : undefined;
	}
	const parsed = DateTime.fromISO(value);
	return parsed.isValid ? parsed : undefined;
};

/**
 * The first and last date of a displayed week.
 *
 * Each week BLOCK of `dates` is seven contiguous days from a week-start-aligned date, so a week's own
 * boundaries are always its first and seventh entries whatever `week_start_day` the department runs. No
 * `weekStartDay` argument here on purpose: needing one would mean the block was not aligned. The blocks
 * themselves are NOT in date order — see `displayedDateSpan`.
 */
export const weekRangeOf = (dates: GridDates, weekIdx: number): [DateTime | undefined, DateTime | undefined] => [
	toGridDateTime(dates, weekIdx * DAYS_PER_WEEK),
	toGridDateTime(dates, weekIdx * DAYS_PER_WEEK + DAYS_PER_WEEK - 1),
];

/**
 * The date under one DAY CELL — and the only correct way to get it.
 *
 * Every grid in this module is keyed by `weekIdx * 7 + dayIdx` with a SUNDAY-based `dayIdx`, while
 * `dates` runs in COLUMN order from the department's own week start. Indexing `dates` with `dayIdx`
 * therefore reads the right date only for a Sunday-start department and silently reads a date up to six
 * days off for every other one. The conversion lives here so no call site can skip it.
 */
export const dayDateOf = (dates: GridDates, weekIdx: number, dayIdx: number, weekStartDay: number): DateTime | undefined =>
	toGridDateTime(dates, weekIdx * DAYS_PER_WEEK + dayIdxToColumn(dayIdx, weekStartDay));

/** A day cell's date as the user reads it ("21 Sep"). */
// DEFAULT: "" for a missing date. `dates` has exactly `gridWeeks * 7` entries and comes from the same
// payload field as `gridWeeks`, so every cell the grid draws has one and this branch cannot be reached.
// Silence rather than a throw because the only caller is a render path behind Layout's ErrorBoundary,
// whose fallback is `null`: a backend contract break would blank the whole Hiring page instead of
// dropping one date from a label. Loud belongs on the wire contract, not on the label formatter.
export const formatGridDayDate = (date: DateTime | undefined): string => (date ? date.toFormat(DAY_DATE_FORMAT) : "");

/**
 * The earliest and latest date on screen.
 *
 * NOT `dates[0]` and `dates[last]`: the array is in COLUMN order, and the week the department is
 * currently in always takes its own cycle column, so week 1 can be the later of the two. Reading the
 * ends would print the span backwards whenever that happens.
 */
export const displayedDateSpan = (dates: GridDates): [DateTime | undefined, DateTime | undefined] => {
	const parsed = dates.map((_, index) => toGridDateTime(dates, index)).filter((date): date is DateTime => date !== undefined);
	if (parsed.length === 0) {
		return [undefined, undefined];
	}
	return [parsed.reduce((a, b) => (a < b ? a : b)), parsed.reduce((a, b) => (a > b ? a : b))];
};

/**
 * Whether a displayed week is the one today falls in, or a later one.
 *
 * Compared on CALENDAR DATES in the department's own timezone rather than on instants: the payload's
 * dates are department-midnight and "is this the week we are in" is a question about the department's
 * day, not the viewer's. Both ends of the active window are INCLUSIVE.
 *
 * TWO states, the same pair the Master Schedule's header uses, because the backend now picks the same
 * weeks it does: `_compute_display_dates` advances every column to the soonest occurrence of its cycle
 * week on or after the current one, so the grid always spans exactly the next `gridWeeks` weeks and
 * nothing it draws is behind us. (A `past` state existed while the dates came straight off
 * `_compute_anchor_date`, which stepped back a week whenever today fell in cycle-week 2.)
 */
export type WeekState = "active" | "upcoming";

export const weekStateOf = (dates: GridDates, weekIdx: number, timezone: string): WeekState => {
	const [weekStart] = weekRangeOf(dates, weekIdx);
	// DEFAULT: "upcoming" for a week with no start date. Unreachable for the same reason as
	// `formatGridDayDate`'s "" — `dates` is `gridWeeks * 7` long — and silent for the same reason: this
	// runs inside the grid's render, under an ErrorBoundary whose fallback is `null`.
	if (!weekStart) {
		return "upcoming";
	}
	const today = DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd");
	return today >= weekStart.toFormat("yyyy-MM-dd") ? "active" : "upcoming";
};

/**
 * The week caption, mirroring the Master Schedule's: `Week 1 (active 21–27 Sep)`.
 *
 * One helper rather than the reference grid's JSX-level concatenation, for two reasons: the gaps table
 * and the Add Hire dialog must not drift apart, and the word order belongs to the translator — the
 * Master Schedule hardcodes English ordering, which reads backwards in Hebrew.
 *
 * The state word comes from the shared `active` / `upcoming` vocabulary, not a hiring-scoped copy, so
 * this header and the Master Schedule's read identically where they overlap.
 */
export const formatWeekHeader = (dates: GridDates, weekIdx: number, weekNumber: number, timezone: string, t: Translate): string => {
	const [weekStart, weekEnd] = weekRangeOf(dates, weekIdx);
	return t("hiring.gaps.weekHeader", {
		week: weekNumber,
		state: t(weekStateOf(dates, weekIdx, timezone)),
		range: formatWeekRangeWithMonth(weekStart, weekEnd),
	});
};
